import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../utils/api';
import { publisherSelfAPI, dispatchAPI } from '../../../utils/api';
import Swal from 'sweetalert2';
import '../../workorders/css/workorderModal.css';
import './dispatchNoteForm.css';

// helper endpoints (reuse existing api)
const fetchProjects = () => api.get('/api/projects').then(res => res.data.content || []);
const fetchWorkOrders = projectId => api.get('/api/work-order').then(res=>{
  const arr = res.data || [];
  return arr.filter(w => w.project && String(w.project.id) === String(projectId));
});

const normalizeWorkOrder = w => ({
  ...w,
  orderNumber: w.orderNumber || w.order_number,
  orderDate: w.orderDate || w.order_date,
  totalQuantity: w.totalQuantity ?? w.total_quantity ?? 0,
  totalPrice: w.totalPrice ?? w.total_price ?? 0,
  package: w.package || w.Package,
  publisher: w.publisher || w.Publisher,
  class: w.class || w.ClassLevel,
  items: (w.items || []).map(it => ({
    ...it,
    bookId: it.bookId || it.book_id,
    unitPrice: it.unitPrice ?? it.unit_price ?? 0,
  })),
});

const initialState = {
  workOrderNo: '',
  truckNumber: '',
  driverName: '',
  driverCnic: '',
  eta: '',
  projectId: '',
  referenceNo: '',
};

export default function DispatchNoteForm({isPublisher=false}) {
  const [form, setForm] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [validate, setValidate] = useState({});
  const [projects, setProjects] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [books, setBooks] = useState([]);
  const [viewOrder, setViewOrder] = useState(null);
  const [orderItems,setOrderItems]=useState([]);
  const navigate = useNavigate();
  const nf = new Intl.NumberFormat('en-US');

  // referenceNo will be generated by backend

  useEffect(() => {
    fetchProjects().then(setProjects).catch(console.error);
  }, []);

  // load work orders when project changes (internal) or once for publisher
  useEffect(()=>{
    if(isPublisher){
      publisherSelfAPI.getWorkOrders().then(r=>setWorkOrders((r.data||[]).map(normalizeWorkOrder))).catch(console.error);
    }
  },[isPublisher]);

  useEffect(() => {
    if(!isPublisher){
      if (form.projectId) {
        fetchWorkOrders(form.projectId).then(rows => setWorkOrders(rows.map(normalizeWorkOrder))).catch(console.error);
      } else {
        setWorkOrders([]);
      }
    }
  }, [form.projectId,isPublisher]);

  // load books once for modal details
  useEffect(()=>{ api.get('/api/v1/book').then(r=>setBooks(r.data||[])).catch(()=>{}); },[]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleProjectChange = e => {
    setForm({ ...form, projectId: e.target.value, workOrderNo: '' });
  };

  const handleWorkOrderChange = e => {
    const orderNo = e.target.value;
    const selected = workOrders.find(w => w.orderNumber === orderNo);
    setForm(prev => ({
      ...prev,
      workOrderNo: orderNo,
      quantity: selected ? selected.totalQuantity : '',
      classLevel: selected && selected.class ? selected.class.name : prev.classLevel,
    }));

    // populate items table
    if(selected && selected.items){
      const rows = selected.items.map(it=>{
        const b = books.find(x=>x.id===it.bookId) || {};
        return {
          bookId: it.bookId,
          bookCode: b.bookCode || it.bookId,
          bookName: b.name || '',
          balanceQty: it.quantity,
          dispatchQty: it.quantity,
        };
      });
      setOrderItems(rows);
    } else {
      setOrderItems([]);
    }

    // fetch next reference
    if(orderNo){
      dispatchAPI.nextRef(orderNo).then(r=>{
        setForm(p=>({...p, referenceNo: r.data.referenceNo }));
      }).catch(()=>{});
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // validation
    const req=[...(isPublisher?[]:['projectId']), 'workOrderNo','truckNumber','driverName','driverCnic','eta'];
    const vObj={}; let ok=true;
    req.forEach(f=>{ if(!form[f]){ vObj[f]=true; ok=false; } });
    // items must exist with dispatchQty>0
    if(orderItems.length===0){ ok=false; vObj.items=true; }
    orderItems.forEach((it,idx)=>{ if(!it.dispatchQty || it.dispatchQty<=0){ ok=false; vObj[`item_${idx}`]=true;} });
    setValidate(vObj);
    if(!ok){
      setError('Please fill all required fields highlighted in red');
      setTimeout(()=>{
        const firstInvalid = document.querySelector('.is-invalid');
        if(firstInvalid) firstInvalid.focus();
      },0);
      return;
    }
    setLoading(true);
    setError(null);
    let attempt = 0;
    const base = form.workOrderNo;
    const payload = { ...form };
    delete payload.referenceNo;
    if(orderItems.length>0){
      payload.items = orderItems.map(r=>({bookId:r.bookId,dispatchQty:r.dispatchQty,balanceQty:r.balanceQty}));
    }
    while (attempt < 5) {
      try {
        const created = await api.post('/api/v1/inventory/dispatch-notes', payload);
        const noteId = created.data?.id;
        await Swal.fire({
          icon:'success',
          title:`Dispatch Note ${created.data?.reference_no || form.referenceNo} created`,
          text:'Opening printable QR receipt.',
          timer:1500,
          showConfirmButton:false
        });
        if (noteId) window.open(`${api.defaults.baseURL}/api/v1/inventory/dispatch-notes/${noteId}/pdf`, '_blank');

        // refresh work orders to update balances
        if(isPublisher){
          publisherSelfAPI.getWorkOrders().then(r=>setWorkOrders((r.data||[]).map(normalizeWorkOrder))).catch(()=>{});
        }else if(form.projectId){
          fetchWorkOrders(form.projectId).then(rows=>setWorkOrders(rows.map(normalizeWorkOrder))).catch(()=>{});
        }

        navigate('/dispatch-notes');
        return;
      } catch (err) {
        const msg = err.response?.data?.message || '';
        if (msg.includes('Duplicate')) {
          attempt++;
          payload.workOrderNo = `${base}-${attempt}`;
        } else {
          setError(msg || err.message);
          break;
        }
      }
    }
    if (attempt>=5) setError('Could not generate unique dispatch note number');
    setLoading(false);
  };

  return (
    <div className="inventory-page">
    <div className="dispatch-note-container premium-card inventory-page-card">
      <h2>Create Dispatch Note</h2>
      {error && <div className="alert alert-danger">{error}</div>}
      <form onSubmit={handleSubmit}>
        <div className="dispnote-grid">

          {/* Project dropdown */}
          {!isPublisher && (
            <div className="dispnote-field">
              <label className="form-label">Project</label>
              <select className={`form-select ${validate.projectId?'is-invalid':''}`} name="projectId" value={form.projectId} onChange={handleProjectChange} required>
                <option value="">Select project</option>
                {projects.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Work Order dropdown */}
          <div className="dispnote-field">
            <label className="form-label">Work Order #</label>
            <div className="project-wo-row">
            <select className={`form-select ${validate.workOrderNo?'is-invalid':''}`} style={{flex:1}} name="workOrderNo" value={form.workOrderNo} onChange={handleWorkOrderChange} required disabled={!isPublisher ? !form.projectId : false}>
              <option value="">Select work order</option>
              {workOrders.map(w => (
                <option key={w.id} value={w.orderNumber}>{w.orderNumber}</option>
              ))}
            </select>
            <button type="button" className="btn btn-secondary" disabled={!form.workOrderNo} onClick={()=>setViewOrder(workOrders.find(w=>w.orderNumber===form.workOrderNo))}>View</button>
            </div>
          </div>

          {/* Reference number */}
          <div className="dispnote-field">
            <label className="form-label">Reference #</label>
            <input className="form-control" value={form.referenceNo} readOnly />
          </div>

          {form.workOrderNo && (()=>{const w=workOrders.find(x=>x.orderNumber===form.workOrderNo); if(!w) return null; return (<>
      <div className="dispnote-field"><label>Package</label><input readOnly value={w.package?.name||''}/></div>
      <div className="dispnote-field"><label>Publisher</label><input readOnly value={w.publisher?.name||''}/></div>
      <div className="dispnote-field"><label>Total Quantity</label><input readOnly value={w.totalQuantity||0}/></div>
      <div className="dispnote-field"><label>Total Price</label><input readOnly value={nf.format(w.totalPrice||0)}/></div>
    </>);})()}

          <div className="dispnote-field">
            <label className="form-label">Truck #</label>
            <input className={`form-control ${validate.truckNumber?'is-invalid':''}`} name="truckNumber" value={form.truckNumber} onChange={handleChange} />
          </div>
          <div className="dispnote-field">
            <label className="form-label">Driver Name</label>
            <input className={`form-control ${validate.driverName?'is-invalid':''}`} name="driverName" value={form.driverName} onChange={handleChange} />
          </div>
          <div className="dispnote-field">
            <label className="form-label">Driver CNIC</label>
            <input 
              className={`form-control ${validate.driverCnic?'is-invalid':''}`}
              name="driverCnic"
              value={form.driverCnic}
              maxLength={13}
              pattern="\d{13}"
              title="13 digit CNIC required"
              onChange={e=>{
                const val=e.target.value.replace(/[^\d]/g,'');
                setForm({...form, driverCnic: val});
              }}
              required
            />
          </div>
          <div className="dispnote-field">
            <label className="form-label">Expected Arrival (ETA)</label>
            <input type="datetime-local" className={`form-control ${validate.eta?'is-invalid':''}`} name="eta" value={form.eta} onChange={handleChange} />
          </div>

          {/* Items table */}
          {orderItems.length>0 && (
            <div style={{gridColumn:'1/-1',marginTop:16}}>
              <h4>Books</h4>
              <table className="eduss-global-datatable" style={{minWidth:'600px'}}>
                <thead><tr><th>Book Code</th><th>Book Name</th><th>Balance Qty</th><th>Dispatch Qty</th></tr></thead>
                <tbody>
                  {orderItems.map((r,idx)=>(
                    <tr key={r.bookId}>
                      <td>{r.bookCode}</td>
                      <td>{r.bookName||books.find(b=>b.bookCode===r.bookCode)?.name||'-'}</td>
                      <td>{r.balanceQty}</td>
                      <td><input className={`form-control ${validate[`item_${idx}`]?'is-invalid':''}`} type="number" min="0" max={r.balanceQty} value={r.dispatchQty}
                          onChange={e=>{
                            let val=parseInt(e.target.value||0,10);
                            if(val>r.balanceQty) val=r.balanceQty;
                            setOrderItems(itms=>itms.map((it,i)=>i===idx?{...it,dispatchQty:val}:it));
                          }} style={{width:100}}/></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? 'Saving...' : 'Generate Dispatch Note'}
        </button>
      </form>

      {/* View Work Order modal */}
      {viewOrder && (
        <div className="eduss-global-modal-overlay" onClick={()=>setViewOrder(null)}>
          <div className="eduss-global-modal eduss-admin-form-modal" onClick={e=>e.stopPropagation()}>
            <div className="eduss-global-modal-header"><h3>Work Order Details</h3><button onClick={()=>setViewOrder(null)}>&times;</button></div>
            <div className="eduss-global-modal-content" style={{maxHeight:'70vh',overflowY:'auto'}}>
              <table className="eduss-global-view-table"><tbody>
                <tr><th>ID</th><td>{viewOrder.id}</td></tr>
                <tr><th>Order #</th><td>{viewOrder.orderNumber}</td></tr>
                <tr><th>Order Date</th><td>{viewOrder.orderDate}</td></tr>
                <tr><th>Vendor</th><td>{viewOrder.vendor?.name}</td></tr>
                <tr><th>Package</th><td>{viewOrder.package?.name}</td></tr>
                <tr><th>Class</th><td>{viewOrder.class?.name}</td></tr>
                <tr><th>Total Qty</th><td>{viewOrder.totalQuantity}</td></tr>
                <tr><th>Status</th><td>{viewOrder.status}</td></tr>
              </tbody></table>

              {viewOrder.items && viewOrder.items.length>0 && (
                <div className="workorder-modal-table" style={{marginTop:12}}>
                  <table className="eduss-global-datatable" style={{minWidth:'1000px'}}>
                    <thead><tr><th>Book Code</th><th>Book Name</th><th>Qty</th><th>Unit Price</th><th>Subtotal</th></tr></thead>
                    <tbody>{viewOrder.items.map(it=>{ const b=books.find(x=>x.id===it.bookId)||{}; return (
                      <tr key={it.bookId}><td>{b.bookCode||'-'}</td><td>{b.name||it.name}</td><td>{it.quantity}</td><td>{it.unitPrice}</td><td>{(it.unitPrice*it.quantity).toFixed(2)}</td></tr>
                    );})}</tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
    </div>
  );
}
